import { useState, useEffect } from 'react';
import { getPatients } from '../../services/api';
import api from '../../services/api';

const STATUT_OPTIONS = [
    { value: 'a_contacter', icon: 'ti-phone',   label: 'À contacter' },
    { value: 'en_attente',  icon: 'ti-clock',   label: 'En attente' },
    { value: 'confirme',    icon: 'ti-check',   label: 'Confirmé' },
    { value: 'prepare',     icon: 'ti-package', label: 'Préparé' },
    { value: 'annule',      icon: 'ti-x',       label: 'Annulé' },
];

const TYPE_OPTIONS = [
    { value: 'standard',       icon: 'ti-box',     label: 'Standard' },
    { value: 'recup',          icon: 'ti-refresh', label: 'Récupération' },
    { value: 'installation',   icon: 'ti-tool',    label: 'Installation' },
    { value: 'envoi_tnt',      icon: 'ti-truck',   label: 'Envoi TNT' },
    { value: 'envoi_postal',   icon: 'ti-mail',    label: 'Envoi Postal' },
    { value: 'visite_patient', icon: 'ti-home',    label: 'Visite patient' },
];

const PATHOLOGIE_ICONS = {
    nutrition: '🟢',
    perfusion: '🔵',
    parkinson: '⚫',
};

export default function DeliveryEditForm({ delivery, onSave, onCancel, onDelete, can = () => true  }) {
    const [patients, setPatients] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [form, setForm] = useState({
        patient: delivery.patient?.['@id'] || (delivery.patient?.id ? `/api/patients/${delivery.patient.id}` : '') || '',
        livreur: delivery.livreur?.['@id'] || (delivery.livreur?.id ? `/api/users/${delivery.livreur.id}` : '') || '',
        date: delivery.date?.substring(0, 10) || '',
        periode: delivery.periode || 'matin',
        consigneHoraire: delivery.consigneHoraire || '',
        statutPrep: delivery.statutPrep || 'a_contacter',
        typelivraison: delivery.typelivraison || 'standard',
        notes: delivery.notes || '',
        imuno: delivery.imuno || false,
        adresseSecondaireChoisie: delivery.adresseSecondaireChoisie || false,
        livreur2: delivery.livreur2?.['@id'] || '',
        alerte: delivery.alerte || false,
    });
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [patientsRes, usersRes] = await Promise.all([
                    getPatients(),
                    api.get('/users'),
                ]);
                setPatients(patientsRes.data.member || []);
                setUsers(usersRes.data.member || []);
            } catch (err) {
                console.error(err);
            }

            try {
                const logsRes = await api.get(`/logs?entite=Delivery&entiteId=${delivery.id}&action=created&order[createdAt]=asc`);
                setLogs(logsRes.data.member || []);
            } catch {
                // non bloquant
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedPatient = patients.find(p => p['@id'] === form.patient);

    useEffect(() => {
        if (!selectedPatient) return;
        if (selectedPatient.isRetro) {
            setForm(f => ({ ...f, statutPrep: 'prepare' }));
        }
    }, [selectedPatient]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.patch(`/deliveries/${delivery.id}`, {
                patient: form.patient,
                livreur: form.livreur,
                date: form.date,
                periode: form.periode,
                consigneHoraire: form.consigneHoraire || null,
                statutPrep: form.statutPrep,
                typelivraison: form.typelivraison,
                notes: form.notes,
                imuno: form.imuno,
                adresseSecondaireChoisie: form.adresseSecondaireChoisie,
                livreur2: form.livreur2 || null,
                alerte: form.alerte,
            }, {
                headers: { 'Content-Type': 'application/merge-patch+json' }
            });
            onSave();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        try {
            // Si livraison récurrente, on crée une exception avant de supprimer
            if (delivery.recurrence) {
                const recurrenceId = typeof delivery.recurrence === 'string'
                    ? delivery.recurrence.split('/').pop()
                    : delivery.recurrence.id;
                await api.post('/recurrence_exceptions', {
                    recurrence: `/api/recurrences/${recurrenceId}`,
                    dateExclue: delivery.dateOrigine?.substring(0, 10) || delivery.date?.substring(0, 10),
                }, {
                    headers: { 'Content-Type': 'application/ld+json' }
                });
            }
            await api.delete(`/deliveries/${delivery.id}`);
            onDelete();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* Section : Patient */}
            <div style={sectionStyle}>
                <div style={{display: 'flex', gap: '12px'}}>
                    <div style={{...fieldStyle, flex: 1}}>
                        <label style={labelStyle}>Patient *</label>
                        <select name="patient" value={form.patient} onChange={handleChange} style={inputStyle} required>
                            <option value="">Sélectionner</option>
                            {patients.map(p => (
                                <option key={p['@id']} value={p['@id']}>
                                    {PATHOLOGIE_ICONS[p.pathologie] || '⚪'} {p.nom} — {p.ville}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{...fieldStyle, flex: 1}}>
                        <label style={labelStyle}>Livreur *</label>
                        <select name="livreur" value={form.livreur} onChange={handleChange} style={inputStyle} required>
                            <option value="">Sélectionner</option>
                            {users.map(u => (
                                <option key={u['@id']} value={u['@id']}>{u.nom || u.email}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{...fieldStyle, flex: 1}}>
                        <label style={labelStyle}>Livreur 2 (optionnel)</label>
                        <select name="livreur2" value={form.livreur2} onChange={handleChange} style={inputStyle}>
                            <option value="">Aucun</option>
                            {users.map(u => (
                                <option key={u['@id']} value={u['@id']}>{u.nom || u.email}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {selectedPatient?.adresseSecondaire && (
                    <div style={{...fieldStyle, marginBottom: 0}}>
                        <label style={labelStyle}><i className="ti ti-map-pin" style={{marginRight: '4px'}}/>Adresse de livraison</label>
                        <div style={{display: 'flex', gap: '6px'}}>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, adresseSecondaireChoisie: false })}
                                style={{
                                    flex: 1, padding: '8px 10px', borderRadius: '6px',
                                    border: `1.5px solid ${!form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#e0e0e0'}`,
                                    backgroundColor: !form.adresseSecondaireChoisie ? '#ede9fe' : 'white',
                                    color: !form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#888',
                                    fontSize: '12px', fontWeight: !form.adresseSecondaireChoisie ? '700' : '500',
                                    cursor: 'pointer', textAlign: 'left',
                                }}
                            >
                                🏠 Principale
                                <div style={{ fontSize: '10px', fontWeight: '400', marginTop: '2px', opacity: 0.8 }}>
                                    {selectedPatient.adresse}, {selectedPatient.ville}
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, adresseSecondaireChoisie: true })}
                                style={{
                                    flex: 1, padding: '8px 10px', borderRadius: '6px',
                                    border: `1.5px solid ${form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#e0e0e0'}`,
                                    backgroundColor: form.adresseSecondaireChoisie ? '#ede9fe' : 'white',
                                    color: form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#888',
                                    fontSize: '12px', fontWeight: form.adresseSecondaireChoisie ? '700' : '500',
                                    cursor: 'pointer', textAlign: 'left',
                                }}
                            >
                                🏡 Secondaire
                                <div style={{ fontSize: '10px', fontWeight: '400', marginTop: '2px', opacity: 0.8 }}>
                                    {selectedPatient.adresseSecondaire}{selectedPatient.villeSecondaire ? `, ${selectedPatient.villeSecondaire}` : ''}
                                </div>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Section : Détails */}
            <div style={sectionStyle}>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ ...fieldStyle, flex: 1 }}>
                        <label style={labelStyle}>Date *</label>
                        <input type="date" name="date" value={form.date} onChange={handleChange} style={inputStyle} required />
                    </div>
                    <div style={{ ...fieldStyle, flex: 1 }}>
                        <label style={labelStyle}>Période *</label>
                        <select name="periode" value={form.periode} onChange={handleChange} style={inputStyle}>
                            <option value="matin">Matin</option>
                            <option value="aprem">Après-midi</option>
                        </select>
                    </div>
                    <div style={{ ...fieldStyle, flex: 1.4 }}>
                        <label style={labelStyle}>⏰ Consigne horaire</label>
                        <input
                            type="text"
                            name="consigneHoraire"
                            value={form.consigneHoraire}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="Ex: avant 10h..."
                        />
                    </div>
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Statut préparation</label>
                    <div style={{display: 'flex', gap: '6px'}}>
                        {STATUT_OPTIONS.map(opt => {
                            const active = form.statutPrep === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    title={opt.label}
                                    onClick={() => setForm({...form, statutPrep: opt.value})}
                                    style={{
                                        flex: 1, height: '38px', padding: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '8px',
                                        border: `1.5px solid ${active ? 'var(--accent-color)' : '#e0e0e0'}`,
                                        backgroundColor: active ? '#ede9fe' : 'white',
                                        color: active ? 'var(--accent-color)' : '#888',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <i className={`ti ${opt.icon}`} aria-hidden="true"></i>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div style={{ ...fieldStyle, marginBottom: 0 }}>
                    <label style={labelStyle}>Type de livraison</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {TYPE_OPTIONS.map(opt => {
                            const active = form.typelivraison === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    title={opt.label}
                                    onClick={() => {
                                        const autoStatuts = ['recup', 'visite_patient', 'standard'];
                                        const newStatut = autoStatuts.includes(opt.value) ? 'prepare' : form.statutPrep;
                                        setForm({ ...form, typelivraison: opt.value, statutPrep: newStatut });
                                    }}
                                    style={{
                                        flex: 1, height: '38px', padding: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '8px',
                                        border: `1.5px solid ${active ? 'var(--accent-color)' : '#e0e0e0'}`,
                                        backgroundColor: active ? '#ede9fe' : 'white',
                                        color: active ? 'var(--accent-color)' : '#888',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <i className={`ti ${opt.icon}`} aria-hidden="true"></i>
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            title="Livraison avec immuno"
                            onClick={() => setForm({ ...form, imuno: !form.imuno })}
                            style={{
                                flex: 1, height: '38px', padding: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '8px',
                                border: '1.5px solid #f59e0b',
                                backgroundColor: form.imuno ? '#fef3c7' : 'white',
                                color: form.imuno ? '#d97706' : '#888',
                                fontSize: '16px',
                                cursor: 'pointer',
                            }}
                        >
                            <i className="ti ti-star" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Section : Options */}
            <div style={sectionStyle}>
                <div style={{ ...fieldStyle, marginBottom: 0 }}>
                    <label style={labelStyle}>Notes</label>
                    <textarea
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        style={{ ...inputStyle, height: '54px', resize: 'vertical' }}
                    />
                </div>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    {can('supprimer_livraison') && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            style={{
                                padding: '7px 12px',
                                backgroundColor: confirmDelete ? '#ef4444' : 'transparent',
                                color: confirmDelete ? 'white' : '#ef4444',
                                border: '1px solid #ef4444',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                            }}
                        >
                            {confirmDelete
                                ? <><i className="ti ti-alert-triangle" style={{marginRight: '4px'}}/>Confirmer</>
                                : <><i className="ti ti-trash" style={{marginRight: '4px'}}/>Supprimer</>
                            }
                        </button>
                    )}
                    {confirmDelete && delivery.recurrence && (
                        <span style={{ fontSize: '11px', color: '#d97706' }}><i className="ti ti-alert-triangle" style={{ marginRight: '3px' }} />Non recréée</span>
                    )}
                </div>

                <div style={{display: 'flex', gap: '10px'}}>
                <button type="button" onClick={onCancel} style={cancelButtonStyle}>Annuler</button>
                    <button type="submit" style={submitButtonStyle} disabled={loading}>
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
            {/* Footer — historique */}
            {logs.length > 0 && (
                <div style={{
                    marginTop: '12px',
                    paddingTop: '10px',
                    borderTop: '1px solid #f0f0f0',
                    fontSize: '11px',
                    color: '#aaa',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}>
        <span>
            <i className="ti ti-pencil" style={{ marginRight: '4px' }} />Créé le {new Date(logs[0].createdAt).toLocaleDateString('fr-FR')} par {logs[0].user?.nom || '?'}
        </span>
                    {delivery.recurrence && (
                        <span><i className="ti ti-repeat" style={{ marginRight: '4px' }} />R#{typeof delivery.recurrence === 'string'
                            ? delivery.recurrence.split('/').pop()
                            : delivery.recurrence.id}</span>
                    )}
                </div>
            )}
        </form>
    );
}

const sectionStyle = {
    borderLeft: '3px solid #4f46e5',
    paddingLeft: '14px',
    marginBottom: '16px',
};
const fieldStyle = { marginBottom: '12px' };
const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#444' };
const inputStyle = {
    width: '100%',
    padding: '8px 11px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '13px',
    boxSizing: 'border-box',
};
const cancelButtonStyle = {
    padding: '9px 18px',
    backgroundColor: '#f0f2f5',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
};
const submitButtonStyle = {
    padding: '9px 18px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
};